import { useState, useEffect, useRef, useMemo } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useWindowWidth } from "../../hooks/useWindowWidth";
import { DARK_THEME, LIGHT_THEME, DARK_STATUS_STYLES, LIGHT_STATUS_STYLES } from "../../theme";
import { DARK_TOKENS, LIGHT_TOKENS, RADIUS } from "../../theme/tokens";
import { useThemeContext } from "../../theme/ThemeContext";
import { AirlineLogo, StarMark, SA_AIRLINES, LHG_CARRIERS, Button } from "../shared";
import { fmtConn, getAirlineCode } from "../../utils/timeUtils";
import { dbSetTyping, dbClearTyping, dbGetTyping, uploadAttachment, getAttachmentUrl, downloadAttachmentBlob, deleteAttachment } from "../../db";
import { terminalFallback } from "../../utils/entryUtils";
import { overlayBackdrop } from "../../theme/styles";
import { useSwipeToClose } from "../../hooks/useSwipeToClose";

const ATTACH_IMAGE_EXTS = /\.(jpg|jpeg|png|gif|webp|heic)$/i;
const ATTACH_MAX_BYTES = 10 * 1024 * 1024;

function AttachmentViewer({ path, name, SA, tokens, TYPOGRAPHY }) {
  const [displayUrl, setDisplayUrl] = useState(null);
  const [linkUrl, setLinkUrl] = useState(null);
  const isImage = ATTACH_IMAGE_EXTS.test(name || path);

  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    if (isImage) {
      // Fetch signed URL for tap-to-open in browser
      getAttachmentUrl(path).then(u => { if (!cancelled) setLinkUrl(u); });
      // Download as blob for inline display — avoids Capacitor WKWebView load failures
      downloadAttachmentBlob(path).then(blob => {
        if (cancelled || !blob) return;
        blobUrl = URL.createObjectURL(blob);
        setDisplayUrl(blobUrl);
      });
    } else {
      getAttachmentUrl(path).then(u => { if (!cancelled) setDisplayUrl(u); });
    }
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [path, isImage]);

  if (!displayUrl) return (
    <div style={{ marginTop: 8, fontSize: TYPOGRAPHY.tiny, color: SA.silver, fontStyle: "italic" }}>
      Loading attachment…
    </div>
  );
  if (isImage) return (
    <a href={linkUrl || displayUrl} target="_blank" rel="noopener noreferrer" style={{ display: "block", marginTop: 8 }}>
      <img src={displayUrl} alt={name || "attachment"} style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, display: "block", cursor: "pointer" }} />
    </a>
  );
  return (
    <a href={displayUrl} target="_blank" rel="noopener noreferrer"
      style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8,
        padding: "6px 10px", borderRadius: RADIUS.md, background: "rgba(255,255,255,0.08)",
        border: `1px solid ${SA.borderGray}`, color: SA.white, fontSize: TYPOGRAPHY.caption,
        textDecoration: "none", wordBreak: "break-all" }}>
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
        <path d="M4 2h6l4 4v9H4V2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
        <path d="M10 2v4h4" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
      </svg>
      {name || "Download"}
    </a>
  );
}

const SA_CARRIERS = ["LHG","AC","TK","NH","NZ","OZ","CA","TP","LO","MS","SK","SQ","TG","UA","ZH","A3","AI","AV","AZ","BR","ET"];
// Full-name lookup for carrier picker display (supplements SA_AIRLINES)
const SA_CARRIER_NAMES = {
  ...Object.fromEntries(SA_AIRLINES.map(a => [a.code, a.name])),
  LHG: "Lufthansa Group",
  AV: "Avianca",
};

function AmenityBubble({ m, isUA, onAmenityResponse, tokens, SA, TYPOGRAPHY }) {
  const [action,             setAction]             = useState(null);  // 'approved' | 'denied'
  const [delayType,          setDelayType]          = useState("controllable");
  const [requestContactInfo, setRequestContactInfo] = useState(false);
  const [passengerNames,     setPassengerNames]     = useState([""]);
  const [denialReason,       setDenialReason]       = useState("");
  const [saving,             setSaving]             = useState(false);

  const updateName = (i, val) => setPassengerNames(prev => prev.map((n, idx) => idx === i ? val : n));
  const addName    = () => setPassengerNames(prev => [...prev, ""]);
  const removeName = (i) => setPassengerNames(prev => prev.filter((_, idx) => idx !== i));

  const typeLabel = m.amenity_types === "both" ? "Hotel + Meals"
    : m.amenity_types === "hotel" ? "Hotel"
    : m.amenity_types === "meals" ? "Meals"
    : m.amenity_types || "—";

  const statusColor = m.amenity_status === "approved"
    ? tokens.success
    : m.amenity_status === "denied"
      ? tokens.danger
      : tokens.warning;

  const statusLabel = m.amenity_status === "approved"
    ? `Approved · ${m.amenity_delay_type || ""}`
    : m.amenity_status === "denied"
      ? `Denied · ${m.amenity_delay_type || ""}`
      : "Awaiting Response";

  const handleConfirm = async () => {
    setSaving(true);
    const names = requestContactInfo ? passengerNames.filter(n => n.trim()) : [];
    const reason = action === "denied" ? denialReason.trim() : "";
    await onAmenityResponse(m, action, delayType, action === "approved" && requestContactInfo, names, reason);
    setSaving(false);
    setAction(null);
  };

  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <rect x="2" y="7" width="12" height="7" rx="1" stroke="currentColor" strokeWidth="1.3"/>
          <path d="M5 7V5.5C5 3.567 6.567 2 8 2C9.433 2 11 3.567 11 5.5V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          <path d="M2 10.5H14" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeDasharray="2 2"/>
        </svg>
        <span style={{ fontSize: TYPOGRAPHY.caption, fontWeight: 700, letterSpacing: "0.1em",
          textTransform: "uppercase", color: SA.lightSilver }}>
          Amenity Request
        </span>
      </div>
      {/* Type */}
      <div style={{ fontSize: 15, color: SA.white, fontWeight: 500, marginBottom: m.body ? 6 : 10 }}>
        {typeLabel}
      </div>
      {/* Optional note */}
      {m.body ? (
        <div style={{ fontSize: TYPOGRAPHY.sub, color: SA.silver, marginBottom: 10, lineHeight: 1.5 }}>
          {m.body}
        </div>
      ) : null}
      {/* Status chip */}
      <div style={{ display: "inline-flex", alignItems: "center", gap: 5,
        padding: "4px 10px", borderRadius: 20, marginBottom: action ? 12 : 0,
        background: m.amenity_status === "approved"
          ? "rgba(110,196,114,0.12)"
          : m.amenity_status === "denied"
            ? "rgba(220,50,50,0.1)"
            : "rgba(200,168,48,0.1)",
        border: `1px solid ${m.amenity_status === "approved"
          ? "rgba(110,196,114,0.35)"
          : m.amenity_status === "denied"
            ? "rgba(220,50,50,0.3)"
            : "rgba(200,168,48,0.3)"}`,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: statusColor, display: "inline-block", flexShrink: 0 }} />
        <span style={{ fontSize: TYPOGRAPHY.label, fontWeight: 700, letterSpacing: "0.06em",
          textTransform: "uppercase", color: statusColor }}>
          {statusLabel}
        </span>
      </div>
      {/* SCCO approve/deny controls — only when pending */}
      {isUA && m.amenity_status === "pending" && (
        <div style={{ marginTop: 12 }}>
          {action === null ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setAction("approved")}
                style={{ flex: 1, padding: "8px", borderRadius: 8, cursor: "pointer",
                  background: "rgba(110,196,114,0.1)", border: "1px solid rgba(110,196,114,0.35)",
                  color: tokens.success, fontSize: TYPOGRAPHY.caption, fontWeight: 700,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                Approve
              </button>
              <button onClick={() => setAction("denied")}
                style={{ flex: 1, padding: "8px", borderRadius: 8, cursor: "pointer",
                  background: "rgba(220,50,50,0.08)", border: "1px solid rgba(220,50,50,0.3)",
                  color: tokens.danger, fontSize: TYPOGRAPHY.caption, fontWeight: 700,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                Deny
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.08em",
                textTransform: "uppercase", marginBottom: 8 }}>
                Delay type
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                {["controllable", "uncontrollable"].map(dt => (
                  <button key={dt} onClick={() => setDelayType(dt)}
                    style={{ flex: 1, padding: "7px", borderRadius: 8, cursor: "pointer",
                      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                      fontSize: TYPOGRAPHY.label, fontWeight: 600, letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      background: delayType === dt ? "rgba(255,255,255,0.1)" : "transparent",
                      border: delayType === dt ? `1px solid ${SA.lightSilver}` : `1px solid ${SA.borderGray}`,
                      color: delayType === dt ? SA.white : SA.silver }}>
                    {dt}
                  </button>
                ))}
              </div>
              {action === "denied" && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: "block", fontSize: TYPOGRAPHY.label, color: SA.silver,
                    letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6,
                    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                    Reason (optional)
                  </label>
                  <textarea
                    value={denialReason}
                    onChange={e => setDenialReason(e.target.value)}
                    placeholder="e.g. due to ATC delays in SFO"
                    rows={2}
                    style={{ width: "100%", boxSizing: "border-box",
                      background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                      borderRadius: 6, color: SA.white, padding: "8px 10px",
                      fontSize: TYPOGRAPHY.sub, outline: "none", resize: "none", lineHeight: 1.5,
                      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}
                  />
                </div>
              )}
              {action === "approved" && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: requestContactInfo ? 10 : 0,
                    cursor: "pointer", userSelect: "none" }}>
                    <input type="checkbox" checked={requestContactInfo}
                      onChange={e => { setRequestContactInfo(e.target.checked); if (!e.target.checked) setPassengerNames([""]); }}
                      style={{ width: 16, height: 16, accentColor: tokens.partner, cursor: "pointer" }} />
                    <span style={{ fontSize: TYPOGRAPHY.label, color: SA.lightSilver,
                      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                      Request passenger contact info
                    </span>
                  </label>
                  {requestContactInfo && (
                    <div>
                      <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.06em",
                        marginBottom: 6, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                        Passengers missing contact info:
                      </div>
                      {passengerNames.map((name, i) => (
                        <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
                          <input value={name} onChange={e => updateName(i, e.target.value)}
                            placeholder={`Passenger ${i + 1} name`}
                            style={{ flex: 1, background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                              borderRadius: 6, color: SA.white, padding: "7px 10px",
                              fontSize: TYPOGRAPHY.sub, outline: "none",
                              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }} />
                          {passengerNames.length > 1 && (
                            <button onClick={() => removeName(i)}
                              style={{ background: "none", border: "none", color: SA.silver,
                                cursor: "pointer", fontSize: 18, padding: "0 2px", lineHeight: 1 }}>×</button>
                          )}
                        </div>
                      ))}
                      <button onClick={addName}
                        style={{ background: "none", border: `1px dashed ${SA.borderGray}`,
                          color: SA.silver, borderRadius: 6, padding: "5px 10px", cursor: "pointer",
                          fontSize: TYPOGRAPHY.label, width: "100%", marginTop: 2,
                          fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                        + Add passenger
                      </button>
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setAction(null); setRequestContactInfo(false); setDenialReason(""); }}
                  style={{ flex: 1, padding: "8px", borderRadius: 8, cursor: "pointer",
                    background: "transparent", border: `1px solid ${SA.borderGray}`,
                    color: SA.silver, fontSize: TYPOGRAPHY.caption,
                    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                  Back
                </button>
                <button onClick={handleConfirm} disabled={saving}
                  style={{ flex: 2, padding: "8px", borderRadius: 8, cursor: saving ? "default" : "pointer",
                    background: action === "approved" ? "rgba(110,196,114,0.15)" : "rgba(220,50,50,0.1)",
                    border: `1px solid ${action === "approved" ? "rgba(110,196,114,0.4)" : "rgba(220,50,50,0.35)"}`,
                    color: action === "approved" ? tokens.success : tokens.danger,
                    fontSize: TYPOGRAPHY.caption, fontWeight: 700, letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                  {saving ? "Saving..." : `Confirm ${action === "approved" ? "Approval" : "Denial"}`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ContactInfoRequestBubble({ m, isUA, messages, onProvide, tokens, SA, TYPOGRAPHY }) {
  const alreadyResponded = messages.some(
    r => r.msg_type === "contact_info_response" && r.connection_id === m.connection_id
  );
  const passengerNames = (m.body || "").split("\n").filter(Boolean);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <rect x="1.5" y="3.5" width="13" height="9" rx="1" stroke="currentColor" strokeWidth="1.3"/>
          <path d="M1.5 5.5L8 9.5L14.5 5.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        </svg>
        <span style={{ fontSize: TYPOGRAPHY.caption, fontWeight: 700, letterSpacing: "0.1em",
          textTransform: "uppercase", color: SA.lightSilver }}>
          Contact Info Request
        </span>
      </div>
      <div style={{ fontSize: TYPOGRAPHY.sub, color: SA.silver, marginBottom: passengerNames.length ? 8 : 12, lineHeight: 1.5 }}>
        {isUA
          ? "Passenger contact information requested from partner for amenity voucher delivery."
          : "United is requesting email addresses for the following passengers:"}
      </div>
      {passengerNames.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {passengerNames.map((name, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: SA.silver,
                display: "inline-block", flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: TYPOGRAPHY.sub, color: SA.white,
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                {name}
              </span>
            </div>
          ))}
        </div>
      )}
      {!isUA && (
        alreadyResponded ? (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 5,
            padding: "4px 10px", borderRadius: 20,
            background: "rgba(110,196,114,0.12)", border: "1px solid rgba(110,196,114,0.35)" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: tokens.success, display: "inline-block" }} />
            <span style={{ fontSize: TYPOGRAPHY.label, fontWeight: 700, letterSpacing: "0.06em",
              textTransform: "uppercase", color: tokens.success }}>
              Contact info provided ✓
            </span>
          </div>
        ) : (
          <button onClick={() => onProvide(m)}
            style={{ padding: "8px 16px", borderRadius: 8, cursor: "pointer",
              background: tokens.partnerSurface, border: `1px solid ${tokens.partnerBorder}`,
              color: tokens.partner, fontSize: TYPOGRAPHY.caption, fontWeight: 700,
              letterSpacing: "0.08em", textTransform: "uppercase",
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
            Provide Contact Info
          </button>
        )
      )}
    </div>
  );
}

function ContactInfoResponseBubble({ m, SA, TYPOGRAPHY }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.3"/>
          <path d="M5.5 8L7.5 10L10.5 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span style={{ fontSize: TYPOGRAPHY.caption, fontWeight: 700, letterSpacing: "0.1em",
          textTransform: "uppercase", color: SA.lightSilver }}>
          Passenger Contacts
        </span>
      </div>
      <div style={{ fontSize: 14, color: SA.white, whiteSpace: "pre-wrap", lineHeight: 1.7,
        fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
        {m.body}
      </div>
    </div>
  );
}

function ContactInfoModal({ requestMsg, myAirline, appDate, thread, onClose, onSend, SA, tokens, TYPOGRAPHY, GLASS }) {
  const requestedNames = (requestMsg?.body || "").split("\n").filter(Boolean);
  const hasNames = requestedNames.length > 0;

  const [contacts, setContacts] = useState(() =>
    hasNames
      ? requestedNames.map(n => ({ name: n, email: "" }))
      : [{ name: "", email: "" }]
  );
  const [submitting, setSubmitting] = useState(false);

  const updateEmail = (i, val) =>
    setContacts(prev => prev.map((c, idx) => idx === i ? { ...c, email: val } : c));
  const updateContact = (i, field, val) =>
    setContacts(prev => prev.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
  const addRow    = () => setContacts(prev => [...prev, { name: "", email: "" }]);
  const removeRow = (i) => setContacts(prev => prev.filter((_, idx) => idx !== i));

  const canSubmit = contacts.some(c => c.email.trim());

  const handleSubmit = async () => {
    setSubmitting(true);
    const lines = contacts
      .filter(c => c.email.trim())
      .map((c, i) => {
        const nameStr = c.name.trim() ? `${c.name.trim()}  ·  ` : "";
        return `${i + 1}. ${nameStr}${c.email.trim()}`;
      });
    await onSend({
      date: appDate,
      from_airline: myAirline,
      to_airline: thread,
      body: lines.join("\n"),
      urgent: false,
      read: false,
      connection_id: requestMsg.connection_id,
      msg_type: "contact_info_response",
    });
    setSubmitting(false);
    onClose();
  };

  const emailInputStyle = {
    flex: 1, background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
    borderRadius: 6, color: SA.white, padding: "8px 10px",
    fontSize: TYPOGRAPHY.sub, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
    outline: "none", minWidth: 0,
  };

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 220,
        background: "rgba(0,0,0,0.6)", display: "flex",
        alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ ...GLASS.modal, width: "100%", maxWidth: 420,
          borderRadius: 16, maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.18em",
            textTransform: "uppercase" }}>
            Passenger Contact Info
          </div>
          <button onClick={onClose}
            style={{ background: SA.medGray, border: "none", color: SA.silver,
              width: 28, height: 28, borderRadius: "50%", cursor: "pointer",
              fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
            ×
          </button>
        </div>
        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginBottom: 14, lineHeight: 1.5 }}>
            {hasNames
              ? "Enter the email address for each passenger listed below."
              : "Enter name and email for each passenger requiring amenity vouchers."}
          </div>
          {contacts.map((c, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              {hasNames ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, width: 16,
                    textAlign: "right", flexShrink: 0 }}>{i + 1}.</span>
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                    <div style={{ fontSize: TYPOGRAPHY.sub, color: SA.lightSilver,
                      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                      padding: "6px 0" }}>
                      {c.name}
                    </div>
                    <input value={c.email} onChange={e => updateEmail(i, e.target.value)}
                      placeholder="Email address" style={emailInputStyle} />
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, width: 16,
                    textAlign: "right", flexShrink: 0 }}>{i + 1}.</span>
                  <input value={c.name} onChange={e => updateContact(i, "name", e.target.value)}
                    placeholder="Name" style={emailInputStyle} />
                  <input value={c.email} onChange={e => updateContact(i, "email", e.target.value)}
                    placeholder="Email" style={emailInputStyle} />
                  {contacts.length > 1 && (
                    <button onClick={() => removeRow(i)}
                      style={{ background: "none", border: "none", color: SA.silver,
                        cursor: "pointer", fontSize: 18, flexShrink: 0, lineHeight: 1, padding: "0 2px" }}>
                      ×
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          {!hasNames && (
            <button onClick={addRow}
              style={{ background: "none", border: `1px dashed ${SA.borderGray}`,
                color: SA.silver, borderRadius: 8, padding: "7px 14px", cursor: "pointer",
                fontSize: TYPOGRAPHY.caption, letterSpacing: "0.06em",
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                width: "100%", marginTop: 4 }}>
              + Add passenger
            </button>
          )}
        </div>
        {/* Footer */}
        <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${SA.borderGray}`,
          display: "flex", gap: 10 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: "12px", borderRadius: 8, cursor: "pointer",
              background: "transparent", border: `1px solid ${SA.borderGray}`,
              color: SA.silver, fontSize: TYPOGRAPHY.caption, letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={!canSubmit || submitting}
            style={{ flex: 2, padding: "12px", borderRadius: 8,
              cursor: canSubmit && !submitting ? "pointer" : "default",
              background: canSubmit && !submitting ? tokens.partnerSurface : SA.darkGray,
              border: `1px solid ${canSubmit && !submitting ? tokens.partnerBorder : SA.borderGray}`,
              color: canSubmit && !submitting ? tokens.partner : SA.silver,
              fontSize: TYPOGRAPHY.body, fontWeight: 700, letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
              transition: "all 0.2s" }}>
            {submitting ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessagesModal({ messages, isUA, isAdmin, myAirline, appDate, entries, onClose, onSend, onBroadcast, onDelete, onDeleteAll, onMarkRead, broadcastReceipts = [], onRecordBroadcastReceipts, onLoadBroadcastReceipts, onOpenEntry, openThread, initialLinkEntry, isDark, GLASS, onAmenityResponse }) {
  const SA = isDark ? DARK_THEME : LIGHT_THEME;
  const STATUS_STYLES = isDark ? DARK_STATUS_STYLES : LIGHT_STATUS_STYLES;
  const tokens = isDark ? DARK_TOKENS : LIGHT_TOKENS;
  const { TYPOGRAPHY } = useThemeContext();

  useLockBodyScroll();
  const [thread, setThread]             = useState(null);
  // Jump straight to a specific thread when opened via notification tap
  useEffect(() => { if (openThread) setThread(openThread); }, [openThread]);
  const [body, setBody]                 = useState("");
  const [urgent, setUrgent]             = useState(false);
  const [linkEntry, setLinkEntry]       = useState(initialLinkEntry || null);
  const [contactInfoMsg, setContactInfoMsg] = useState(null);
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [sending, setSending]           = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [showComposePicker, setShowComposePicker] = useState(false);
  const [showBroadcastCompose, setShowBroadcastCompose] = useState(false);
  const [broadcastBody, setBroadcastBody] = useState("");
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [viewingLinked, setViewingLinked] = useState(null);
  const [theyAreTyping, setTheyAreTyping] = useState(false);
  const [attachFile, setAttachFile]       = useState(null);
  const [attachUploading, setAttachUploading] = useState(false);
  const fileInputRef = useRef(null);
  const winWidth = useWindowWidth();
  const isWide = winWidth >= 640;
  const { handleProps, dragStyle } = useSwipeToClose(onClose, { enabled: !isWide });
  const messagesEndRef = useRef(null);
  const typingTimeout = useRef(null);
  const isTyping = useRef(false);

  // Carriers with existing threads — collapse LHG subsidiaries into LHG, sort by most recent message
  const normaliseCarrier = (c) => LHG_CARRIERS.includes(c) ? "LHG" : c;
  const myId = isUA ? "UA" : myAirline;
  const activeCarriers = (() => {
    const latestByCarrier = {};
    // For UA: only consider messages where UA is a participant (exclude partner-to-partner)
    const relevant = isUA
      ? messages.filter(m => m.from_airline === "UA" || m.to_airline === "UA")
      : messages;
    relevant.forEach(m => {
      const other = m.from_airline === myId ? m.to_airline : m.from_airline;
      if (other === myId) return;
      if (other === "ALL") return;
      const carrier = isUA ? normaliseCarrier(other) : other;
      const t = new Date(m.created_at).getTime();
      if (!latestByCarrier[carrier] || t > latestByCarrier[carrier]) latestByCarrier[carrier] = t;
    });
    return Object.keys(latestByCarrier).sort((a, b) => latestByCarrier[b] - latestByCarrier[a]);
  })();

  // Compose picker: UA excludes itself, partners exclude themselves (UA stays in list for partners)
  // Sorted UA-first, then alphabetically by full name
  const allCarriers = useMemo(() =>
    SA_CARRIERS
      .filter(c => c !== myId)
      .sort((a, b) => {
        if (a === "UA") return -1;
        if (b === "UA") return 1;
        return (SA_CARRIER_NAMES[a] || a).localeCompare(SA_CARRIER_NAMES[b] || b);
      }),
    [myId]
  );

  // Admin UA monitor: partner-to-partner thread pairs (neither side is UA)
  // Thread key format: "NH|AC" (sorted alphabetically for uniqueness)
  const partnerThreadPairs = (isAdmin && isUA) ? (() => {
    const pairs = new Map();
    messages.forEach(m => {
      if (m.from_airline === "UA" || m.to_airline === "UA") return;
      const pair = [m.from_airline, m.to_airline].sort();
      const key = pair.join("|");
      const t = new Date(m.created_at).getTime();
      if (!pairs.has(key) || t > pairs.get(key).t) {
        pairs.set(key, { key, t, lastMsg: m });
      }
    });
    return [...pairs.values()].sort((a, b) => b.t - a.t);
  })() : [];

  // Broadcast messages — to_airline = "ALL"
  const broadcastMessages = messages
    .filter(m => m.to_airline === "ALL")
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const hasBroadcasts = broadcastMessages.length > 0;
  const broadcastUnread = !isUA
    ? broadcastMessages.filter(m => !broadcastReceipts.some(r => r.message_id === m.id)).length
    : 0;

  // A monitor thread is a partner-to-partner thread opened by an admin UA SCCO
  const isMonitorThread = !!(thread && thread.includes("|"));
  const monitorPair = isMonitorThread ? thread.split("|") : null;
  const isBroadcastThread = thread === "BROADCAST";

  // When UA opens the LHG thread, match messages from any LHG subsidiary
  const threadCodes = !isMonitorThread && isUA && thread === "LHG" ? ["LHG", ...LHG_CARRIERS] : [thread];
  const threadMessages = thread
    ? isBroadcastThread
      ? broadcastMessages
      : isMonitorThread
        ? messages
            .filter(m => monitorPair.includes(m.from_airline) && monitorPair.includes(m.to_airline))
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        : messages.filter(m =>
            (m.from_airline === myId && threadCodes.includes(m.to_airline)) ||
            (m.to_airline   === myId && threadCodes.includes(m.from_airline))
          ).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    : [];

  // Auto-scroll to latest message — instant on thread open, smooth on new messages
  const prevThread = useRef(null);
  useEffect(() => {
    const behavior = prevThread.current !== thread ? "instant" : "smooth";
    prevThread.current = thread;
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, [thread, threadMessages.length]);

  // Mark messages as read when thread opens
  // Also backfill read_at for messages already marked read but missing the timestamp
  useEffect(() => {
    if (!thread || isMonitorThread || isBroadcastThread) return;
    const myAirlineId = isUA ? "UA" : myAirline;
    const toMark = threadMessages
      .filter(m => m.to_airline === myAirlineId && (!m.read || !m.read_at))
      .map(m => m.id);
    if (toMark.length) onMarkRead(toMark);
  }, [thread]);

  // When partner opens broadcast thread — record receipts for all broadcast messages
  useEffect(() => {
    if (!isBroadcastThread || isUA || !onRecordBroadcastReceipts) return;
    const ids = broadcastMessages.map(m => m.id);
    if (ids.length) onRecordBroadcastReceipts(ids);
  }, [isBroadcastThread]); // eslint-disable-line react-hooks/exhaustive-deps -- fires once when broadcast thread opens; broadcastMessages/callbacks are stable at that point

  // When UA opens broadcast thread — load who has seen each message
  useEffect(() => {
    if (!isBroadcastThread || !isUA || !onLoadBroadcastReceipts) return;
    const ids = broadcastMessages.map(m => m.id);
    if (ids.length) onLoadBroadcastReceipts(ids);
  }, [isBroadcastThread]); // eslint-disable-line react-hooks/exhaustive-deps -- fires once when broadcast thread opens; broadcastMessages/callbacks are stable at that point

  // Poll for typing indicator every 2.5s when thread is open
  useEffect(() => {
    if (!thread || !appDate || isMonitorThread || isBroadcastThread) return;
    const myId = isUA ? "UA" : myAirline;
    let cancelled = false;
    const checkTyping = () => {
      dbGetTyping(myId, appDate).then(rows => {
        if (cancelled) return;
        // Find if the other party in this thread is typing
        const other = rows.find(r => r.airline === thread);
        if (other) {
          const age = (Date.now() - new Date(other.updated_at).getTime()) / 1000;
          setTheyAreTyping(age < 6); // stale after 6s
        } else {
          setTheyAreTyping(false);
        }
      });
    };
    checkTyping();
    const interval = setInterval(checkTyping, 2500);
    return () => { cancelled = true; clearInterval(interval); };
  }, [thread, appDate]);

  // Clear our typing indicator when modal closes or thread changes
  useEffect(() => {
    return () => {
      if (isTyping.current && thread) {
        dbClearTyping(isUA ? "UA" : myAirline, thread);
        isTyping.current = false;
      }
    };
  }, [thread]);

  const handleBodyChange = (val) => {
    setBody(val);
    if (!thread) return;
    const myId = isUA ? "UA" : myAirline;
    // Set typing in DB
    dbSetTyping(myId, thread, appDate);
    isTyping.current = true;
    // Clear after 4s of no typing
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      dbClearTyping(myId, thread);
      isTyping.current = false;
    }, 4000);
  };

  const handleSend = async () => {
    if (!body.trim() && !attachFile) return;
    if (!thread) return;
    // Clear typing indicator on send
    clearTimeout(typingTimeout.current);
    dbClearTyping(isUA ? "UA" : myAirline, thread);
    isTyping.current = false;
    setSending(true);
    let attachmentUrl = null;
    let attachmentName = null;
    if (attachFile) {
      setAttachUploading(true);
      const path = await uploadAttachment(attachFile, isUA ? "UA" : myAirline);
      setAttachUploading(false);
      if (path) { attachmentUrl = path; attachmentName = attachFile.name; }
    }
    await onSend({
      date: appDate,
      from_airline: isUA ? "UA" : myAirline,
      to_airline: thread,
      body: body.trim(),
      urgent,
      read: false,
      connection_id: linkEntry ? linkEntry.id : null,
      attachment_url: attachmentUrl,
      attachment_name: attachmentName,
    });
    setBody("");
    setUrgent(false);
    setLinkEntry(null);
    setAttachFile(null);
    setSending(false);
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  };

  // For message bubbles: show time only if today, otherwise "Apr 1 · 14:35"
  const fmtMsgTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const timeStr = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
    const today = new Date().toLocaleDateString("en-CA");
    const msgDay = d.toLocaleDateString("en-CA");
    if (msgDay === today) return timeStr;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " · " + timeStr;
  };

  // For thread list tiles: time if today, short date if older
  const fmtThreadTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const today = new Date().toLocaleDateString("en-CA");
    const msgDay = d.toLocaleDateString("en-CA");
    if (msgDay === today) return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const unreadFor = (carrier) => messages.filter(m =>
    !m.read && m.from_airline === carrier && m.to_airline === (isUA ? "UA" : myAirline)
  ).length;

  const totalUnread = messages.filter(m => {
    if (isUA) return !m.read && m.to_airline === "UA";
    return !m.read && (m.to_airline === myAirline || m.to_airline === "ALL");
  }).length;

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 200,
      display: "flex", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
      alignItems: isWide ? "center" : "flex-end",
      justifyContent: isWide ? "center" : "stretch",
      padding: isWide ? 24 : 0 }}>
    <div onClick={e => e.stopPropagation()} style={{
      width: "100%",
      maxWidth: isWide ? 700 : "100%",
      height: isWide ? "82vh" : "78vh",
      ...GLASS.modal,
      borderRadius: isWide ? 20 : "20px 20px 0 0",
      display: "flex", overflow: "hidden",
      position: "relative",
      animation: isWide ? "modalSlideUp 0.28s cubic-bezier(0.16,1,0.3,1)" : "sheetSlideUp 0.32s cubic-bezier(0.16,1,0.3,1)",
      ...dragStyle
    }}>

      {!isWide && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 10,
                      display: "flex", justifyContent: "center", padding: "10px 0 4px",
                      touchAction: "none", cursor: "grab" }} {...handleProps}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: SA.borderGray }} />
        </div>
      )}
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      {(isWide || !thread) && (
      <div style={{ width: isWide ? 230 : "100%", background: "transparent",
        borderRight: isWide ? `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}` : "none",
        display: "flex", flexDirection: "column", flexShrink: 0 }}>

        {/* Sidebar header */}
        <div style={{ padding: "22px 18px 16px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 15, color: SA.white, fontWeight: 600, letterSpacing: "-0.01em" }}>Messages</div>
            {totalUnread > 0 && (
              <div style={{ fontSize: TYPOGRAPHY.caption, color: tokens.danger, marginTop: 2, fontWeight: 500 }}>
                {totalUnread} unread
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {isAdmin && messages.length > 0 && (
              confirmDeleteAll ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Button variant="ghost" onClick={() => setConfirmDeleteAll(false)}
                    style={{ fontSize: TYPOGRAPHY.label, color: SA.borderGray, padding: "3px 6px", fontFamily: "'Barlow',sans-serif" }}>
                    Cancel
                  </Button>
                  <Button variant="ghost" onClick={async () => { await onDeleteAll(); setConfirmDeleteAll(false); }}
                    style={{ fontSize: TYPOGRAPHY.label, color: tokens.danger, padding: "3px 6px", fontFamily: "'Barlow',sans-serif", fontWeight: 600 }}>
                    Confirm
                  </Button>
                </div>
              ) : (
                <Button variant="ghost" onClick={() => setConfirmDeleteAll(true)}
                  style={{ fontSize: TYPOGRAPHY.label, color: SA.borderGray, padding: "3px 6px", fontFamily: "'Barlow',sans-serif",
                    letterSpacing: "0.04em" }}>
                  Delete All
                </Button>
              )
            )}
            {!isWide && (
              <Button variant="icon" onClick={onClose}>×</Button>
            )}
          </div>
        </div>

        {/* Thread list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {/* Broadcast pinned entry — visible to all when broadcasts exist */}
          {hasBroadcasts && (() => {
            const lastBroadcast = broadcastMessages[broadcastMessages.length - 1];
            return (
              <Button variant="tile" active={thread === "BROADCAST"} onClick={() => setThread("BROADCAST")}
                style={{ padding: "12px 16px", gap: 12, textAlign: "left",
                  borderLeft: `3px solid ${tokens.danger}` }}>
                <div style={{ width: 36, height: 36, borderRadius: RADIUS.circle, flexShrink: 0,
                  background: tokens.dangerBg, border: `1px solid ${tokens.dangerBorder}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 18 }}>⚠</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: TYPOGRAPHY.note, color: broadcastUnread > 0 ? SA.white : SA.lightSilver,
                    fontWeight: broadcastUnread > 0 ? 600 : 500 }}>All Partners</div>
                  <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginTop: 1 }}>Broadcast</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                  {lastBroadcast && (
                    <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver,
                      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                      {fmtThreadTime(lastBroadcast.created_at)}
                    </span>
                  )}
                  {broadcastUnread > 0 && (
                    <span style={{ background: tokens.danger, color: "#fff", fontSize: TYPOGRAPHY.tiny,
                      borderRadius: RADIUS.md, padding: "2px 6px", fontWeight: 700 }}>
                      {broadcastUnread}
                    </span>
                  )}
                </div>
              </Button>
            );
          })()}
          {activeCarriers.length === 0 && !hasBroadcasts && (
            <div style={{ padding: "48px 20px", textAlign: "center", color: SA.borderGray,
              fontSize: TYPOGRAPHY.caption, lineHeight: 1.8 }}>
              No messages yet
            </div>
          )}
          {activeCarriers.map(c => {
            const u = unreadFor(c);
            const lastMsg = messages
              .filter(m => (m.from_airline === c && m.to_airline === (isUA ? "UA" : myAirline)) ||
                           (m.to_airline === c && m.from_airline === (isUA ? "UA" : myAirline)))
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
            return (
              <Button key={c} variant="tile" active={thread === c} onClick={() => setThread(c)}
                style={{ padding: "12px 16px", gap: 12, textAlign: "left" }}>
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <AirlineLogo iata={c} size={36} />
                  {u > 0 && (
                    <span style={{ position: "absolute", top: -4, right: -4,
                      background: tokens.danger, color: "#fff", fontSize: TYPOGRAPHY.tiny,
                      borderRadius: RADIUS.md, padding: "2px 5px",
                      fontWeight: 700, minWidth: 16, textAlign: "center", lineHeight: 1.3 }}>
                      {u}
                    </span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: TYPOGRAPHY.note, color: u > 0 ? SA.white : SA.lightSilver,
                    fontWeight: u > 0 ? 600 : 500, letterSpacing: "-0.01em",
                    display: "flex", alignItems: "center", gap: 5, overflow: "hidden" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {SA_CARRIER_NAMES[c] || c}
                    </span>
                    {c === "UA" && <span style={{ fontSize: 7, letterSpacing: "0.10em",
                      textTransform: "uppercase", color: SA.accent, background: "rgba(91,142,255,0.12)",
                      border: `1px solid rgba(91,142,255,0.30)`, borderRadius: RADIUS.sharp,
                      padding: "2px 5px", fontFamily: "'Helvetica Neue',Arial,sans-serif",
                      fontWeight: 700, flexShrink: 0 }}>SCCO</span>}
                  </div>
                  <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginTop: 1,
                    letterSpacing: "0.04em", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                    {c}
                  </div>
                  {lastMsg && (
                    <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginTop: 2,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      maxWidth: 140, fontStyle: "italic" }}>
                      {lastMsg.body}
                    </div>
                  )}
                </div>
                {lastMsg && (
                  <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, flexShrink: 0,
                    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", alignSelf: "flex-start", paddingTop: 2 }}>
                    {fmtThreadTime(lastMsg.created_at)}
                  </div>
                )}
              </Button>
            );
          })}

          {/* Partner-to-partner monitor threads — admin UA only */}
          {partnerThreadPairs.length > 0 && (
            <>
              <div style={{ padding: "10px 16px 4px", display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ flex: 1, height: 1, background: SA.borderGray }} />
                <span style={{ fontSize: 8, color: SA.borderGray, letterSpacing: "0.14em",
                  textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif",
                  fontWeight: 600, whiteSpace: "nowrap" }}>Partner Threads</span>
                <div style={{ flex: 1, height: 1, background: SA.borderGray }} />
              </div>
              {partnerThreadPairs.map(({ key, lastMsg: lm }) => {
                const [pA, pB] = key.split("|");
                return (
                  <Button key={key} variant="tile" active={thread === key} onClick={() => setThread(key)}
                    style={{ padding: "10px 16px", gap: 10, textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                      <AirlineLogo iata={pA} size={24} />
                      <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.borderGray, margin: "0 3px" }}>↔</span>
                      <AirlineLogo iata={pB} size={24} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: TYPOGRAPHY.body, color: SA.lightSilver, fontWeight: 500,
                        letterSpacing: "-0.01em" }}>{pA} ↔ {pB}</div>
                      {lm && (
                        <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, marginTop: 1,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {lm.body}
                        </div>
                      )}
                    </div>
                  </Button>
                );
              })}
            </>
          )}
        </div>

        {/* Compose button */}
        <div style={{ padding: "12px 14px", borderTop: `1px solid ${SA.borderGray}` }}>
          <Button variant="secondary" fullWidth onClick={() => setShowComposePicker(true)}
            style={{ padding: "11px", borderRadius: 12, background: SA.medGray,
              fontSize: TYPOGRAPHY.body, fontWeight: 500, gap: 8 }}>
            + New Thread
          </Button>
          {isUA && (
            <Button fullWidth onClick={() => setShowBroadcastCompose(true)}
              style={{ marginTop: 6, padding: "11px", borderRadius: 12, width: "100%",
                background: tokens.dangerBg, border: `1px solid ${tokens.dangerBorder}`,
                color: tokens.danger, fontSize: TYPOGRAPHY.body, fontWeight: 600, gap: 8 }}>
              Broadcast All
            </Button>
          )}
        </div>
      </div>
      )}

      {/* ── Thread panel ────────────────────────────────────────────────── */}
      {(isWide || thread) && (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>

        {/* Thread header */}
        <div style={{ background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
          padding: "14px 20px", display: "flex", alignItems: "center",
          justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {!isWide && (
              <Button variant="ghost" onClick={() => setThread(null)}
                style={{ fontSize: 22, padding: "0 6px 0 0", lineHeight: 1 }}>
                ‹
              </Button>
            )}
            {thread && !isMonitorThread && !isBroadcastThread && <AirlineLogo iata={thread} size={34} />}
            {isBroadcastThread && (
              <div style={{ width: 34, height: 34, borderRadius: RADIUS.circle,
                background: tokens.dangerBg, border: `1px solid ${tokens.dangerBorder}`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>⚠</div>
            )}
            {isMonitorThread && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <AirlineLogo iata={monitorPair[0]} size={28} />
                <AirlineLogo iata={monitorPair[1]} size={28} />
              </div>
            )}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: TYPOGRAPHY.sub, color: SA.white, fontWeight: 600, letterSpacing: "-0.01em" }}>
                  {isBroadcastThread
                    ? "Broadcast — All Partners"
                    : thread
                      ? isMonitorThread
                        ? `${monitorPair[0]}  ↔  ${monitorPair[1]}`
                        : (SA_CARRIER_NAMES[thread] || thread)
                      : "Select a carrier"}
                </div>
                {isMonitorThread && (
                  <span style={{ fontSize: 8, color: SA.silver, letterSpacing: "0.12em",
                    textTransform: "uppercase", background: SA.darkGray,
                    border: `1px solid ${SA.borderGray}`, borderRadius: RADIUS.sharp,
                    padding: "2px 6px", fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
                    Monitor
                  </span>
                )}
                {isBroadcastThread && (
                  <span style={{ fontSize: 8, color: tokens.danger, letterSpacing: "0.12em",
                    textTransform: "uppercase", background: tokens.dangerBg,
                    border: `1px solid ${tokens.dangerBorder}`, borderRadius: RADIUS.sharp,
                    padding: "2px 6px", fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
                    Broadcast
                  </span>
                )}
                {thread === "UA" && (
                  <span style={{ fontSize: 8, color: SA.accent, letterSpacing: "0.10em",
                    textTransform: "uppercase", background: "rgba(91,142,255,0.12)",
                    border: `1px solid rgba(91,142,255,0.30)`, borderRadius: RADIUS.sharp,
                    padding: "2px 6px", fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 700 }}>
                    SCCO
                  </span>
                )}
              </div>
              {thread && (
                <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginTop: 1 }}>
                  {threadMessages.length} message{threadMessages.length !== 1 ? "s" : ""}
                </div>
              )}
            </div>
          </div>
          <Button variant="icon" onClick={onClose}>×</Button>
        </div>

        {/* Message list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px",
          display: "flex", flexDirection: "column", gap: 16,
          background: "transparent" }}>
          {!thread && (
            <div style={{ textAlign: "center", color: SA.borderGray, fontSize: TYPOGRAPHY.body,
              marginTop: 80, lineHeight: 1.8 }}>
              Select a carrier to view or start a thread
            </div>
          )}
          {threadMessages.map(m => {
            // In monitor threads, treat the second party as "right side" for visual layout
            const isMine = isMonitorThread
              ? m.from_airline === monitorPair[1]
              : m.from_airline === myId;
            const linkedEntry = m.connection_id ? entries.find(e => e.id === m.connection_id) : null;
            const bubbleBg    = m.urgent ? "rgba(122,38,38,0.45)" : isMine
              ? (isDark ? "rgba(91,142,255,0.28)" : "rgba(37,99,235,0.18)")
              : (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.06)");
            const bubbleBorder = m.urgent ? "rgba(200,80,80,0.5)" : isMine
              ? (isDark ? "rgba(91,142,255,0.40)" : "rgba(37,99,235,0.30)")
              : (isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.10)");
            const bubbleRadius = isMine
              ? "18px 18px 4px 18px"
              : "18px 18px 18px 4px";
            return (
              <div key={m.id} style={{ display: "flex", flexDirection: "column",
                alignItems: isMine ? "flex-end" : "flex-start", gap: 4 }}>
                {linkedEntry && (() => {
                  const le = linkedEntry;
                  const les = STATUS_STYLES[le.status] || { bg: SA.darkGray, border: SA.borderGray, text: SA.silver, dot: SA.borderGray };
                  const leInGate  = le.inGate  || terminalFallback(le.inAirline  || (le.inFlt  || "").slice(0,2));
                  const leOutGate = le.outGate || terminalFallback(le.outAirline || (le.outFlt || "").slice(0,2));
                  const leCt = (() => {
                    if (le.cancelled || le.atd === "XCL") return "CNCL";
                    const inT  = le.ata || le.eta || le.sta;
                    const outT = le.atd || le.etd || le.std;
                    if (!inT || !outT) return null;
                    const [ah, am] = inT.split(":").map(Number);
                    const [dh, dm] = outT.split(":").map(Number);
                    if (isNaN(ah) || isNaN(dh)) return null;
                    let diff = (dh * 60 + dm) - (ah * 60 + am);
                    if (diff < 0 && diff + 1440 < 480) diff += 1440;
                    return fmtConn(diff);
                  })();
                  // Urgency glow logic (matches EntryCard)
                  const leNow = new Date();
                  const leTodayISO = leNow.toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
                  const leEffNow = (appDate && appDate < leTodayISO) ? 1440 : (leNow.getHours() * 60 + leNow.getMinutes());
                  const leDepMins = (() => {
                    if (le.status) return null;
                    if (le.atd && le.atd !== "XCL") return null;
                    if (appDate && appDate > leTodayISO) return null;
                    const times = [le.std, le.etd].filter(Boolean);
                    if (!times.length) return null;
                    const toM = t => { const [h, m] = t.split(":").map(Number); return isNaN(h) ? null : h * 60 + m; };
                    const val = Math.max(...times.map(toM).filter(v => v !== null));
                    return val === -Infinity ? null : val;
                  })();
                  const leOverdue = leDepMins !== null && leEffNow > leDepMins;
                  const leUrgent  = leDepMins !== null && !leOverdue && (leDepMins - leEffNow) <= 15;
                  const leTopBorder = leOverdue ? tokens.danger : leUrgent ? "rgba(251,146,60,0.5)" : le.status ? les.border : tokens.warningBorder;
                  const leGateBadge = (g) => (
                    <span style={{ color: SA.accent, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: "0.82em",
                      border: `1px solid ${SA.accent}`, borderRadius: RADIUS.pill, padding: "1px 5px",
                      marginRight: 4, display: "inline-block", lineHeight: 1.4 }}>{g}</span>
                  );
                  return (
                    <div className={`entry-card${leOverdue ? " entry-card-overdue" : leUrgent ? " entry-card-urgent" : ""}`}
                      style={{ maxWidth: "78%", marginBottom: 4 }}>
                      <Button onClick={() => setViewingLinked(le)}
                        className="entry-card-inner"
                        style={{ ...GLASS.card,
                          borderLeft: `4px solid ${leTopBorder}`,
                          borderRadius: RADIUS.md, padding: "10px 12px",
                          textAlign: "left", width: "100%", display: "block" }}>
                        {/* Flight row */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <AirlineLogo iata={le.inAirline || (le.inFlt || "").slice(0,2)} size={26} />
                            <div>
                              <div style={{ fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.sub,
                                color: SA.white, fontWeight: 700, letterSpacing: "0.02em" }}>{le.inFlt}</div>
                              <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.04em", marginTop: 1, textTransform: "uppercase" }}>
                                {leGateBadge(leInGate)}
                                {(le.origin || "").toUpperCase()} › {le.ata ? le.ata + " ATA" : le.eta ? le.eta + " ETA" : le.sta ? le.sta + " STA" : "--"}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, flexShrink: 0 }}>
                            <div style={{ width: 18, height: 1, background: SA.borderGray, opacity: 0.6 }} />
                            <StarMark size={6} color={SA.borderGray} />
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <AirlineLogo iata={le.outAirline || (le.outFlt || "").slice(0,2)} size={26} />
                            <div>
                              <div style={{ fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.sub,
                                color: SA.lightSilver, fontWeight: 700, letterSpacing: "0.02em" }}>{le.outFlt}</div>
                              <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.04em", marginTop: 1, textTransform: "uppercase", whiteSpace: "nowrap" }}>
                                {leGateBadge(leOutGate)}
                                {(le.dest || "").toUpperCase()} › {le.atd ? le.atd + " ATD" : le.etd ? le.etd + " ETD" : le.std ? le.std + " STD" : "--"}
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Status + conn time row */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {[le.status && [le.status, les], le.monitoring && ["Tracking in TDT", STATUS_STYLES.MONITORING]].filter(Boolean).map(([label, bs]) => (
                            <span key={label} style={{ background: bs.bg, color: bs.text, border: `1px solid ${bs.border}`,
                              padding: "3px 8px", fontSize: TYPOGRAPHY.tiny, fontWeight: 700, letterSpacing: "0.22em",
                              textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif",
                              display: "flex", alignItems: "center", gap: 4, borderRadius: 1 }}>
                              <span style={{ width: 5, height: 5, borderRadius: RADIUS.circle, background: bs.dot, display: "inline-block" }} />
                              {label}
                            </span>
                          ))}
                          </span>
                          {leCt && (
                            <span style={{
                              background: leCt === "CNCL" ? "#1a0808" : leCt.startsWith("-") ? "#1a1200" : SA.darkGray,
                              border: `1px solid ${leCt === "CNCL" ? "#7a2a2a" : leCt.startsWith("-") ? tokens.warningBorder : SA.borderGray}`,
                              color: leCt === "CNCL" ? tokens.monoRed : leCt.startsWith("-") ? tokens.warning : SA.white,
                              padding: "3px 8px", borderRadius: RADIUS.pill, fontSize: TYPOGRAPHY.tiny, fontWeight: 700,
                              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", letterSpacing: "0.04em" }}>
                              {leCt}
                              {leCt !== "CNCL" && <span style={{ fontSize: 8, color: SA.silver, marginLeft: 3, fontWeight: 400, letterSpacing: "0.08em" }}>CONN</span>}
                            </span>
                          )}
                        </div>
                        {/* Pax + Bags row */}
                        {(le.ttlPax || le.ttlBags) && (
                          <div style={{ display: "flex", gap: 14, marginTop: 8, paddingTop: 7,
                            borderTop: `1px solid ${SA.borderGray}` }}>
                            {le.ttlPax && (
                              <span style={{ fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.label, color: SA.lightSilver }}>
                                <span style={{ color: SA.silver }}>PAX </span>{le.ttlPax}
                                {le.paxExp ? <span style={{ color: tokens.monoGreen }}> · {le.paxExp} exp</span> : null}
                              </span>
                            )}
                            {le.ttlBags && (
                              <span style={{ fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.label, color: SA.lightSilver }}>
                                <span style={{ color: SA.silver }}>BAGS </span>{le.ttlBags}
                                {le.bagsExp ? <span style={{ color: tokens.monoGreen }}> · {le.bagsExp} exp</span> : null}
                              </span>
                            )}
                          </div>
                        )}
                      </Button>
                    </div>
                  );
                })()}
                {/* Sender + time */}
                <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, letterSpacing: "0.02em",
                  marginBottom: 2, textAlign: isMine ? "right" : "left" }}>
                  {m.from_airline === "UA" && m.from_scco_name ? m.from_scco_name : m.from_airline} · {fmtMsgTime(m.created_at)}
                </div>
                {/* Bubble */}
                <div style={{
                  maxWidth: "88%", padding: "11px 16px",
                  borderRadius: bubbleRadius,
                  background: bubbleBg,
                  border: `1px solid ${bubbleBorder}`,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                }}>
                  {m.urgent && (
                    <div style={{ fontSize: TYPOGRAPHY.body, color: tokens.danger,
                      fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ fontSize: TYPOGRAPHY.sub }}>⚠</span> Urgent
                    </div>
                  )}
                  {m.msg_type === "amenity_request" ? (
                    <AmenityBubble m={m} isUA={isUA} onAmenityResponse={onAmenityResponse}
                      tokens={tokens} SA={SA} TYPOGRAPHY={TYPOGRAPHY} />
                  ) : m.msg_type === "contact_info_request" ? (
                    <ContactInfoRequestBubble m={m} isUA={isUA} messages={messages}
                      onProvide={setContactInfoMsg} tokens={tokens} SA={SA} TYPOGRAPHY={TYPOGRAPHY} />
                  ) : m.msg_type === "contact_info_response" ? (
                    <ContactInfoResponseBubble m={m} SA={SA} TYPOGRAPHY={TYPOGRAPHY} />
                  ) : m.body ? (
                    <div style={{ fontSize: 15, color: SA.white, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                      lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {m.body}
                    </div>
                  ) : null}
                  {m.attachment_url && (
                    <AttachmentViewer path={m.attachment_url} name={m.attachment_name} SA={SA} tokens={tokens} TYPOGRAPHY={TYPOGRAPHY} />
                  )}
                  {!isMonitorThread && (isUA || isMine) && (
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                      {confirmDelete === m.id ? (
                        <div style={{ display: "flex", gap: 8 }}>
                          <Button variant="ghost" onClick={() => { onDelete(m.id); if (m.attachment_url) deleteAttachment(m.attachment_url); setConfirmDelete(null); }}
                            style={{ fontSize: TYPOGRAPHY.label, color: tokens.danger, fontWeight: 600, padding: "0 2px" }}>
                            Delete
                          </Button>
                          <Button variant="ghost" onClick={() => setConfirmDelete(null)}
                            style={{ fontSize: TYPOGRAPHY.label, padding: "0 2px" }}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button variant="ghost" onClick={() => setConfirmDelete(m.id)}
                          style={{ fontSize: TYPOGRAPHY.label, color: SA.borderGray, opacity: 0.7, padding: "0 2px" }}>
                          ···
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {/* Read receipt */}
                {isMine && !isMonitorThread && !isBroadcastThread && (
                  <div style={{ fontSize: TYPOGRAPHY.label, color: m.read ? tokens.success : SA.borderGray,
                    marginTop: 1, textAlign: "right", fontWeight: m.read ? 500 : 400 }}>
                    {m.read
                      ? `✓✓ Read by ${m.to_airline}${m.read_at ? "  " + fmtTime(m.read_at) : ""}`
                      : "Delivered"}
                  </div>
                )}
              </div>
            );
          })}

          <div ref={messagesEndRef} />
        </div>

        {/* Typing indicator — outside scroll, always visible above compose */}
        {theyAreTyping && (
          <div style={{ padding: "8px 16px 0", flexShrink: 0,
            display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.04em" }}>{thread} is typing</div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: 5, height: 5, borderRadius: RADIUS.circle, background: SA.silver,
                  animation: `typingDot 1.2s ease-in-out ${i * 0.2}s infinite`
                }} />
              ))}
            </div>
          </div>
        )}

        {/* Read-only notice for broadcast threads (partners) */}
        {isBroadcastThread && !isUA && (
          <div style={{ padding: "10px 16px", textAlign: "center",
            fontSize: TYPOGRAPHY.caption, color: SA.borderGray,
            borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}` }}>
            Broadcast messages — replies not supported
          </div>
        )}

        {/* Read receipts panel — UA only, broadcast thread */}
        {isBroadcastThread && isUA && broadcastMessages.length > 0 && (() => {
          // Build a set of which airlines have seen at least one broadcast message
          const seenAirlines = new Map(); // airline → most recent read_at
          broadcastReceipts.forEach(r => {
            const key = LHG_CARRIERS.includes(r.airline) ? "LHG" : r.airline;
            const existing = seenAirlines.get(key);
            if (!existing || new Date(r.read_at) > new Date(existing)) {
              seenAirlines.set(key, r.read_at);
            }
          });
          const partners = SA_CARRIERS.filter(c => c !== "UA");
          const allSeen = partners.every(p => seenAirlines.has(p));
          const seenCount = partners.filter(p => seenAirlines.has(p)).length;
          return (
            <div style={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
              padding: "10px 14px 6px" }}>
              <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.16em",
                textTransform: "uppercase", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                marginBottom: 8 }}>
                Read by {seenCount}/{partners.length} partners
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {partners.map(code => {
                  const readAt = seenAirlines.get(code);
                  return (
                    <div key={code} title={readAt ? new Date(readAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Not yet seen"}
                      style={{ display: "flex", alignItems: "center", gap: 4,
                        padding: "3px 8px", borderRadius: RADIUS.pill,
                        border: `1px solid ${readAt ? tokens.successBorder : SA.borderGray}`,
                        background: readAt ? tokens.successSurface : "transparent" }}>
                      <span style={{ fontSize: TYPOGRAPHY.tiny, color: readAt ? tokens.successMuted : SA.silver,
                        fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>{code}</span>
                      {readAt
                        ? <span style={{ fontSize: 9, color: tokens.success }}>✓</span>
                        : <span style={{ fontSize: 9, color: SA.borderGray }}>–</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Compose area */}
        {thread && !isMonitorThread && !isBroadcastThread && (
          <div style={{ background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
            borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
            padding: "12px 16px 16px", flexShrink: 0 }}>
            {/* Link chip */}
            {linkEntry ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
                background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                borderRadius: RADIUS.md, padding: "7px 12px" }}>
                <span style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, flex: 1, fontWeight: 500 }}>
                  🔗 {linkEntry.inFlt} → {linkEntry.outFlt}
                </span>
                <Button variant="ghost" onClick={() => setLinkEntry(null)}
                  style={{ color: SA.borderGray, fontSize: 16, lineHeight: 1 }}>×</Button>
              </div>
            ) : (
              <Button variant="ghost" onClick={() => setShowLinkPicker(true)}
                style={{ marginBottom: 10, border: `1px dashed ${SA.borderGray}`,
                  borderRadius: RADIUS.md, padding: "6px 14px", color: SA.borderGray, fontSize: TYPOGRAPHY.caption,
                  fontWeight: 500 }}>
                + Link connection
              </Button>
            )}

            {/* Attachment chip */}
            {attachFile && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
                background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                borderRadius: RADIUS.md, padding: "7px 12px" }}>
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: SA.silver }}>
                  <path d="M4 2h6l4 4v9H4V2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                  <path d="M10 2v4h4" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                </svg>
                <span style={{ fontSize: TYPOGRAPHY.caption, color: SA.white, flex: 1,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {attachFile.name}
                </span>
                <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, flexShrink: 0 }}>
                  {(attachFile.size / 1024 / 1024).toFixed(1)} MB
                </span>
                <Button variant="ghost" onClick={() => setAttachFile(null)}
                  style={{ color: SA.borderGray, fontSize: 16, lineHeight: 1 }}>×</Button>
              </div>
            )}

            {/* Hidden file input */}
            <input type="file" ref={fileInputRef} style={{ display: "none" }}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) {
                  if (f.size > ATTACH_MAX_BYTES) { alert("File must be under 10 MB"); }
                  else { setAttachFile(f); }
                }
                e.target.value = "";
              }} />

            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <textarea value={body} onChange={e => handleBodyChange(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Message…"
                rows={2}
                style={{ flex: 1, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                  background: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.07)",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.18)" : "rgba(80,100,160,0.22)"}`,
                  borderRadius: RADIUS.card, color: SA.white, padding: "11px 14px", fontSize: 15,
                  fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", resize: "none", outline: "none",
                  lineHeight: 1.5, transition: "border-color 0.2s" }} />
              <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                <Button variant="pill" onClick={() => fileInputRef.current?.click()}
                  style={{ padding: "6px 12px", border: `1px solid ${SA.borderGray}`, color: SA.silver, gap: 5 }}>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <path d="M13.5 9.5V13.5H2.5V9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                    <path d="M8 2.5V10.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                    <path d="M5 5.5L8 2.5L11 5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Attach
                </Button>
                <Button variant="pill" active={urgent} onClick={() => setUrgent(u => !u)}
                  style={{ padding: "6px 12px",
                    background: urgent ? tokens.dangerBgSubtle : "transparent",
                    border: `1px solid ${urgent ? tokens.dangerBorder : SA.borderGray}`,
                    color: urgent ? tokens.danger : SA.silver, gap: 5 }}>
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path d="M6 1.5L11 10.5H1L6 1.5Z" stroke={urgent ? tokens.danger : SA.silver} strokeWidth="1.3" strokeLinejoin="round"/>
                    <path d="M6 5V7.5" stroke={urgent ? tokens.danger : SA.silver} strokeWidth="1.3" strokeLinecap="round"/>
                    <circle cx="6" cy="9" r="0.6" fill={urgent ? tokens.danger : SA.silver}/>
                  </svg>
                  Urgent
                </Button>
                {(() => {
                  const canSend = body.trim() || attachFile;
                  return (
                    <Button variant="primary" onClick={handleSend} disabled={sending || attachUploading || !canSend}
                      style={{ padding: "9px 18px", borderRadius: RADIUS.pill,
                        background: canSend ? SA.accent : "transparent",
                        border: `1px solid ${canSend ? SA.accent : SA.borderGray}`,
                        color: canSend ? "#fff" : SA.borderGray,
                        opacity: (sending || attachUploading) ? 0.6 : 1 }}>
                      {attachUploading ? "↑" : sending ? "···" : "Send"}
                    </Button>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
      )}

    </div>{/* end modal panel */}

      {/* Inline connection viewer — opens within messages modal */}
      {viewingLinked && (() => {
        const e = viewingLinked;
        const s = STATUS_STYLES[e.status] || { bg: SA.darkGray, border: SA.borderGray, text: SA.silver, dot: SA.borderGray };
        const field = (label, val) => val ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ fontSize: 7, color: SA.borderGray, letterSpacing: "0.14em", textTransform: "uppercase",
              fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>{label}</div>
            <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.white, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
              letterSpacing: "0.06em" }}>{val}</div>
          </div>
        ) : null;
        return (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)", zIndex: 210,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ ...GLASS.card, borderRadius: 12,
              width: "100%", maxWidth: 480, maxHeight: "85vh", display: "flex", flexDirection: "column",
              overflowY: "auto" }}>
              {/* Header */}
              <div style={{ padding: "14px 16px", borderBottom: `1px solid ${SA.borderGray}`,
                display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.16em", textTransform: "uppercase",
                  fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>Connection Details</span>
                <Button variant="ghost" onClick={() => setViewingLinked(null)}
                  style={{ fontSize: 18, lineHeight: 1 }}>×</Button>
              </div>
              <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: 12 }}>
                {/* Status */}
                {e.status && (
                  <span style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}`,
                    padding: "4px 10px", fontSize: TYPOGRAPHY.tiny, fontWeight: 700, letterSpacing: "0.12em",
                    textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif",
                    display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 1, alignSelf: "flex-start" }}>
                    <span style={{ width: 5, height: 5, borderRadius: RADIUS.circle, background: s.dot, display: "inline-block" }} />
                    {e.status}
                  </span>
                )}
                {/* Inbound */}
                <div style={{ background: SA.darkGray, border: `1px solid ${SA.borderGray}`, borderRadius: 3, padding: "12px 14px" }}>
                  <div style={{ fontSize: 7, color: SA.borderGray, letterSpacing: "0.14em", textTransform: "uppercase",
                    fontFamily: "'Helvetica Neue',Arial,sans-serif", marginBottom: 10 }}>Inbound</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                    {field("Flight", e.inFlt)}{field("Origin", e.origin)}{field("STA", e.sta)}
                    {field("ETA", e.eta)}{field("ATA", e.ata)}
                  </div>
                </div>
                {/* Outbound */}
                <div style={{ background: SA.darkGray, border: `1px solid ${SA.borderGray}`, borderRadius: 3, padding: "12px 14px" }}>
                  <div style={{ fontSize: 7, color: SA.borderGray, letterSpacing: "0.14em", textTransform: "uppercase",
                    fontFamily: "'Helvetica Neue',Arial,sans-serif", marginBottom: 10 }}>Outbound</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                    {field("Flight", e.outFlt)}{field("Dest", e.dest)}{field("STD", e.std)}
                    {field("ETD", e.etd)}{field("ATD", e.atd)}
                  </div>
                </div>
                {/* PAX / Bags */}
                {(e.ttlPax || e.ttlBags) && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
                    {field("TTL PAX", e.ttlPax)}{field("PAX EXP", e.paxExp)}
                    {field("TTL BAGS", e.ttlBags)}{field("BAGS EXP", e.bagsExp)}
                  </div>
                )}
                {/* Remark */}
                {e.remark && (
                  <div style={{ background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                    borderRadius: 3, padding: "10px 14px" }}>
                    <div style={{ fontSize: 7, color: SA.borderGray, letterSpacing: "0.14em",
                      textTransform: "uppercase", fontFamily: "'Helvetice Neue',Arial,sans-serif", marginBottom: 6 }}>Remarks</div>
                    <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.lightSilver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                      letterSpacing: "0.04em", textTransform: "uppercase", lineHeight: 1.5 }}>{e.remark}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Broadcast compose overlay — UA only */}
      {showBroadcastCompose && (
        <div onClick={e => e.stopPropagation()} style={{ position: "absolute", inset: 0,
          background: "rgba(0,0,0,0.28)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 210,
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ ...GLASS.modal, borderRadius: 16,
            width: "90%", maxWidth: 420, display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${tokens.dangerBorder}`,
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: tokens.dangerBg }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>⚠</span>
                <span style={{ fontSize: TYPOGRAPHY.note, color: tokens.danger, fontWeight: 600 }}>
                  Broadcast to All Partners
                </span>
              </div>
              <Button variant="ghost" onClick={() => { setShowBroadcastCompose(false); setBroadcastBody(""); }}
                style={{ fontSize: 18, lineHeight: 1 }}>×</Button>
            </div>
            <div style={{ padding: "14px 16px" }}>
              <div style={{ fontSize: TYPOGRAPHY.caption, color: tokens.danger, marginBottom: 12,
                padding: "8px 12px", background: tokens.dangerBg, border: `1px solid ${tokens.dangerBorder}`,
                borderRadius: RADIUS.md }}>
                ⚠ This message will be sent to all partner airlines as URGENT
              </div>
              <textarea
                value={broadcastBody}
                onChange={e => setBroadcastBody(e.target.value)}
                placeholder="Broadcast message…"
                rows={4}
                style={{ width: "100%", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                  background: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.07)",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.18)" : "rgba(80,100,160,0.22)"}`,
                  borderRadius: RADIUS.card, color: SA.white, padding: "11px 14px",
                  fontSize: 15, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                  resize: "none", outline: "none", lineHeight: 1.5, boxSizing: "border-box" }}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
                <Button variant="ghost" onClick={() => { setShowBroadcastCompose(false); setBroadcastBody(""); }}
                  style={{ padding: "9px 16px" }}>
                  Cancel
                </Button>
                <Button variant="danger" disabled={broadcastSending || !broadcastBody.trim()}
                  onClick={async () => {
                    if (!broadcastBody.trim()) return;
                    setBroadcastSending(true);
                    await onBroadcast({
                      date: appDate,
                      from_airline: "UA",
                      body: broadcastBody.trim(),
                      read: false,
                      connection_id: null,
                    });
                    setBroadcastBody("");
                    setBroadcastSending(false);
                    setShowBroadcastCompose(false);
                    setThread("BROADCAST");
                  }}
                  style={{ padding: "9px 18px", borderRadius: RADIUS.pill,
                    background: broadcastBody.trim() ? tokens.danger : "transparent",
                    border: `1px solid ${broadcastBody.trim() ? tokens.danger : SA.borderGray}`,
                    color: broadcastBody.trim() ? "#fff" : SA.borderGray,
                    opacity: broadcastSending ? 0.6 : 1 }}>
                  {broadcastSending ? "···" : "Send Broadcast"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compose — carrier picker for new thread */}
      {showComposePicker && (
        <div onClick={e => e.stopPropagation()} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.28)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 210,
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ ...GLASS.modal, borderRadius: 16,
            width: "90%", maxWidth: 400, maxHeight: "70vh", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${SA.borderGray}`,
              display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.16em", textTransform: "uppercase" }}>
                New Message — Select Carrier
              </span>
              <Button variant="ghost" onClick={() => setShowComposePicker(false)}
                style={{ fontSize: 18, lineHeight: 1 }}>×</Button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {allCarriers.map(c => {
                const u = unreadFor(c);
                return (
                  <Button key={c} variant="tile" onClick={(e) => { e.stopPropagation(); setThread(c); setShowComposePicker(false); }}
                    style={{ padding: "14px 16px", borderBottom: `1px solid ${SA.borderGray}`, gap: 14 }}>
                    <AirlineLogo iata={c} size={34} />
                    <span style={{ fontSize: TYPOGRAPHY.note, color: SA.silver, display: "flex", alignItems: "center", gap: 6 }}>
                      {SA_CARRIER_NAMES[c] ? `${c} – ${SA_CARRIER_NAMES[c]}` : c}
                      {c === "UA" && <span style={{ fontSize: 7, letterSpacing: "0.10em",
                        textTransform: "uppercase", color: SA.accent, background: "rgba(91,142,255,0.12)",
                        border: `1px solid rgba(91,142,255,0.30)`, borderRadius: RADIUS.sharp,
                        padding: "2px 5px", fontFamily: "'Helvetica Neue',Arial,sans-serif",
                        fontWeight: 700, flexShrink: 0 }}>SCCO</span>}
                    </span>
                    {u > 0 && (
                      <span style={{ marginLeft: "auto", background: tokens.dangerMuted, color: SA.white, fontSize: 7,
                        borderRadius: RADIUS.md, padding: "1px 5px", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontWeight: 700 }}>
                        {u}
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Link to connection picker */}
      {showLinkPicker && (
        <div onClick={() => setShowLinkPicker(false)} style={{ position: "absolute", inset: 0,
          background: "rgba(0,0,0,0.28)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 210,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ ...GLASS.modal,
            borderRadius: RADIUS.pill, boxShadow: "0 32px 100px rgba(0,0,0,0.95), 0 0 0 1px rgba(255,255,255,0.05)",
            width: "90%", maxWidth: 480, maxHeight: "70vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid ${SA.borderGray}",
              display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.16em", textTransform: "uppercase" }}>
                Link to Connection
              </span>
              <Button variant="icon" onClick={() => setShowLinkPicker(false)}>×</Button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {(() => {
                const threadCarrier = LHG_CARRIERS.includes(thread) ? "LHG" : thread;
                const relevantEntries = entries.filter(e => {
                  const inCode  = getAirlineCode(e.inAirline, e.inFlt);
                  const outCode = getAirlineCode(e.outAirline, e.outFlt);
                  const inGroup  = LHG_CARRIERS.includes(inCode)  ? "LHG" : inCode;
                  const outGroup = LHG_CARRIERS.includes(outCode) ? "LHG" : outCode;
                  return inGroup === threadCarrier || outGroup === threadCarrier;
                });
                if (relevantEntries.length === 0) return (
                  <div style={{ padding: 20, color: SA.borderGray, fontSize: TYPOGRAPHY.tiny, textAlign: "center", letterSpacing: "0.14em" }}>
                    No connections for {thread} today
                  </div>
                );
                return relevantEntries.map(e => {
                  const es = STATUS_STYLES[e.status] || { bg: SA.darkGray, border: SA.borderGray, text: SA.silver, dot: SA.borderGray };
                  const inGate  = e.inGate  || terminalFallback(e.inAirline  || (e.inFlt  || "").slice(0,2));
                  const outGate = e.outGate || terminalFallback(e.outAirline || (e.outFlt || "").slice(0,2));
                  const ct = (() => {
                    if (e.cancelled || e.atd === "XCL") return "CNCL";
                    const inT  = e.ata || e.eta || e.sta;
                    const outT = e.atd || e.etd || e.std;
                    if (!inT || !outT) return null;
                    const [ah, am] = inT.split(":").map(Number);
                    const [dh, dm] = outT.split(":").map(Number);
                    if (isNaN(ah) || isNaN(dh)) return null;
                    let diff = (dh * 60 + dm) - (ah * 60 + am);
                    if (diff < 0 && diff + 1440 < 480) diff += 1440;
                    return fmtConn(diff);
                  })();
                  const gateBadge = (g) => (
                    <span style={{ color: SA.accent, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: "0.82em",
                      border: `1px solid ${SA.accent}`, borderRadius: RADIUS.pill, padding: "1px 5px",
                      marginRight: 4, display: "inline-block", lineHeight: 1.4 }}>{g}</span>
                  );
                  return (
                    <Button key={e.id} onClick={(ev) => { ev.stopPropagation(); setLinkEntry(e); setShowLinkPicker(false); }}
                      style={{ width: "100%", padding: "8px 10px", background: "none", border: "none",
                        borderBottom: `1px solid ${SA.borderGray}`, textAlign: "left", display: "block" }}>
                      <div style={{ ...GLASS.card,
                        borderLeft: `4px solid ${e.status ? es.border : tokens.warningBorder}`,
                        borderRadius: RADIUS.md, padding: "10px 12px" }}>
                        {/* Flight row */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                          {/* Inbound */}
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <AirlineLogo iata={e.inAirline || (e.inFlt || "").slice(0,2)} size={26} />
                            <div>
                              <div style={{ fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.sub,
                                color: SA.white, fontWeight: 700, letterSpacing: "0.02em" }}>{e.inFlt}</div>
                              <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.04em", marginTop: 1, textTransform: "uppercase" }}>
                                {gateBadge(inGate)}
                                {(e.origin || "").toUpperCase()} › {e.ata ? e.ata + " ATA" : e.eta ? e.eta + " ETA" : e.sta ? e.sta + " STA" : "--"}
                              </div>
                            </div>
                          </div>
                          {/* Arrow */}
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, flexShrink: 0 }}>
                            <div style={{ width: 18, height: 1, background: SA.borderGray, opacity: 0.6 }} />
                            <StarMark size={6} color={SA.borderGray} />
                          </div>
                          {/* Outbound */}
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <AirlineLogo iata={e.outAirline || (e.outFlt || "").slice(0,2)} size={26} />
                            <div>
                              <div style={{ fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.sub,
                                color: SA.lightSilver, fontWeight: 700, letterSpacing: "0.02em" }}>{e.outFlt}</div>
                              <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.04em", marginTop: 1, textTransform: "uppercase", whiteSpace: "nowrap" }}>
                                {gateBadge(outGate)}
                                {(e.dest || "").toUpperCase()} › {e.atd ? e.atd + " ATD" : e.etd ? e.etd + " ETD" : e.std ? e.std + " STD" : "--"}
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Status + conn time row */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {[e.status && [e.status, es], e.monitoring && ["Tracking in TDT", STATUS_STYLES.MONITORING]].filter(Boolean).map(([label, bs]) => (
                            <span key={label} style={{ background: bs.bg, color: bs.text, border: `1px solid ${bs.border}`,
                              padding: "3px 8px", fontSize: TYPOGRAPHY.tiny, fontWeight: 700, letterSpacing: "0.22em",
                              textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif",
                              display: "flex", alignItems: "center", gap: 4, borderRadius: 1 }}>
                              <span style={{ width: 5, height: 5, borderRadius: RADIUS.circle, background: bs.dot, display: "inline-block" }} />
                              {label}
                            </span>
                          ))}
                          </span>
                          {ct && (
                            <span style={{
                              background: ct === "CNCL" ? "#1a0808" : ct.startsWith("-") ? "#1a1200" : SA.darkGray,
                              border: `1px solid ${ct === "CNCL" ? "#7a2a2a" : ct.startsWith("-") ? tokens.warningBorder : SA.borderGray}`,
                              color: ct === "CNCL" ? tokens.monoRed : ct.startsWith("-") ? tokens.warning : SA.white,
                              padding: "3px 8px", borderRadius: RADIUS.pill, fontSize: TYPOGRAPHY.tiny, fontWeight: 700,
                              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", letterSpacing: "0.04em" }}>
                              {ct}
                              {ct !== "CNCL" && <span style={{ fontSize: 8, color: SA.silver, marginLeft: 3, fontWeight: 400, letterSpacing: "0.08em" }}>CONN</span>}
                            </span>
                          )}
                        </div>
                      </div>
                    </Button>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}
      {contactInfoMsg && (
        <ContactInfoModal
          requestMsg={contactInfoMsg}
          myAirline={myAirline}
          appDate={appDate}
          thread={thread}
          onClose={() => setContactInfoMsg(null)}
          onSend={async (msg) => { await onSend(msg); }}
          SA={SA} tokens={tokens} TYPOGRAPHY={TYPOGRAPHY} GLASS={GLASS}
        />
      )}
    </div>
  );
}

export { MessagesModal };
